import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { EstimatesV2Service } from '../../../../core/services/estimateV2.service';
import { PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { Round } from '../../../../data/logic/math.functions';
import {
    Estimate,
    Job,
    JobItem,
    JobVariation,
    PurchaseOrder,
    PurchaseOrderCreationSource,
    PurchaseOrderItem,
    QuoteRequest,
    QuoteRequestItem,
    QuoteRequestRecipientItem,
    QuoteStatus,
} from '../../../../data/models';
import { RepoQuoteRequestsService } from '../../../../data/repositories/repo-quoterequests.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { SharedComponentsModule } from '../../../../shared-components.module';
import { PurchaseOrderDetailModule, JobVariationsService, PurchaseOrdersService, JobVariationDetailModule } from '../../../jobs';
import { QuoteRequestsService } from '../../quoterequest.service';
import { BxUnverifiedAlertService } from '../../../../core/services/bx-unverified-alert.service';
import { BreezeCase } from '../../../../utils/types/convert-case';
import { RFQResponseActions } from './rfq-response-actions.enum';
import { RouterModule } from '@angular/router';
import { QuoteRequestListModel, QuoteRequestRecipientListModel } from '../../quoterequest-list/domain/entities/quote-request-list.model';
import { Uuid } from '../../../../../graphql';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { BxSpinnerModule } from '@buildxact/ui-components/bx-spinner';

interface PurchaseOrderItemMap {
    quantity: number;
    totalCost: number;
    order: BreezeCase<PurchaseOrder>;
    orderItem?: BreezeCase<PurchaseOrderItem>;
}

@UntilDestroy()
@Component({
    selector: 'app-ui-quoterequests-recipients-table',
    styleUrls: ['./ui-quoterequests-recipients-table.component.scss'],
    templateUrl: './ui-quoterequests-recipients-table.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        SharedComponentsModule,
        PurchaseOrderDetailModule,
        JobVariationDetailModule,
        BxSpinnerModule,
    ],
})
export class UIQuoteRequestsRecipientsTableComponent implements OnInit {
    #bxUnverifiedAlertService = inject(BxUnverifiedAlertService);
    #permissionService = inject(PermissionService);
    #estimatesService = inject(EstimatesV2Service);
    #jobsRepo = inject(RepoJobsService);
    #storesService = inject(StoresService);
    #quoteReqRepo = inject(RepoQuoteRequestsService);
    #quoteRequestsService = inject(QuoteRequestsService);
    #logger = inject(BxLogger);
    #sharedData = inject(SharedDataService);
    #purchaseOrdersService = inject(PurchaseOrdersService);
    #jobVariationsService = inject(JobVariationsService);
    #dialogService = inject(DialogService);

    row = input.required<QuoteRequestListModel>();
    rowIndex = input.required<number>();
    job = input<Job>();
    estimate = input<Estimate>();

    canSelectRfqResponse = false;
    canReopenRfq = false;

    isLoading = output<boolean>();

    readonly materialCostItem = '0';
    public readonly rfqActions = RFQResponseActions;
    public readonly rfqStatus = QuoteStatus;

    ngOnInit(): void {
        this.canSelectRfqResponse = this.#permissionService.isAvailable('request-for-quote.maintain-rfqs.select-rfq-response');
        this.canReopenRfq = this.#permissionService.isAvailable('request-for-quote.maintain-rfqs.reopen-rfq');
    }

    hasSelectedResponses(quoteRequest: QuoteRequestListModel) {
        return quoteRequest.quoteRequestRecipients?.some((i) => i?.isSelectedQuote) ?? false;
    }

    async onSelectedRecipientChanged(quoteRequestRecipient: QuoteRequestRecipientListModel, quoteRequest: QuoteRequestListModel) {
        this.isLoading.emit(true);
        if (quoteRequestRecipient.isSelectedQuote) {
            quoteRequest.quoteRequestRecipients?.forEach((qrr) => {
                if (qrr && qrr.iD_QuoteRequestRecipient !== quoteRequestRecipient.iD_QuoteRequestRecipient) {
                    qrr.isSelectedQuote = false;
                }
            });
        }
        await this.#quoteRequestsService.onSelectedRecipientChanged(quoteRequestRecipient, quoteRequest);
        this.isLoading.emit(false);
    }

    viewQuoteRequestRecipientResponse(recipient: QuoteRequestRecipientListModel) {
        this.#quoteRequestsService
            .viewQuoteRequestRecipientPanel(recipient.iD_QuoteRequest, recipient.iD_QuoteRequestRecipient)

            .then((updateRecipient: any) => {
                recipient.totalAmount = updateRecipient.totalAmount;
                recipient.quoteStatus = updateRecipient.quoteStatus;
                recipient.dateAccepted = updateRecipient.dateAccepted;
                recipient.dateDeclined = updateRecipient.dateDeclined;
                recipient.dateNotified = updateRecipient.dateNotified;
                recipient.dateOpened = updateRecipient.dateOpened;
                recipient.datePending = updateRecipient.datePending;
                recipient.dateQuoting = updateRecipient.dateQuoting;
                recipient.dateSubmitted = updateRecipient.dateSubmitted;
            })
            .catch(() => {
                // Must have catch otherwise the openAside.reject will throw uncaught error
            });
    }

    async resendQuoteRequest(quoteRequestId: Uuid, quoteRequestRecipientId: Uuid) {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }
        this.isLoading.emit(true);
        await this.#quoteReqRepo.Resend(quoteRequestId, quoteRequestRecipientId);
        this.isLoading.emit(false);
    }

    async remindQuoteRequest(quoteRequestId: Uuid, quoteRequestRecipientId: Uuid) {
        if (!this.#sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.#sharedData.UserData.user);
            return;
        }
        this.isLoading.emit(true);
        await this.#quoteReqRepo.Remind(quoteRequestId, quoteRequestRecipientId);
        this.isLoading.emit(false);
    }

    async acceptQuoteRequest(quoteRequest: QuoteRequestListModel) {
        this.isLoading.emit(true);
        await this.#quoteReqRepo.Accept(quoteRequest.iD_QuoteRequest);
        await this.#quoteReqRepo.GetQuoteRequest(quoteRequest.iD_QuoteRequest);
        this.#logger.success('Supplier notified');
        this.isLoading.emit(false);
        this.#storesService.refreshQuoteRequests();
    }

    async unacceptQuoteRequest(quoteRequest: QuoteRequestListModel) {
        this.isLoading.emit(true);
        await this.#quoteReqRepo.Unaccept(quoteRequest.iD_QuoteRequest);
        await this.#quoteReqRepo.GetQuoteRequest(quoteRequest.iD_QuoteRequest);
        this.#logger.success('Deadline extended for 1 more week.');
        this.isLoading.emit(false);
    }

    async deleteRecipient(evt: Event, recipient: QuoteRequestRecipientListModel) {
        evt.stopPropagation();
        const params = {
            title: 'Delete Recipient?',
            text: `Confirm you want to delete ${recipient.name} from this Quote Request?`,
        };

        try {
            await this.#dialogService.show(params);
            this.isLoading.emit(true);
            await firstValueFrom(this.#estimatesService.deleteQuoteRequestRecipient(recipient.iD_QuoteRequestRecipient));
            this.row().quoteRequestRecipients = this.row().quoteRequestRecipients?.filter(
                (qrr) => qrr?.iD_QuoteRequestRecipient !== recipient.iD_QuoteRequestRecipient
            );
        } catch {
            // cancelled
        } finally {
            this.isLoading.emit(false);
        }
    }

    async viewOrderOrVariation(quoteRequest: QuoteRequestListModel) {
        try {
            this.isLoading.emit(true);

            if (quoteRequest.iD_PurchaseOrder && quoteRequest.iD_Job) {
                if (!this.job() || this.job()!.ID_Job !== quoteRequest.iD_Job) {
                    // Need to load job entity so breeze mapping works here
                    await this.#jobsRepo.GetJob(quoteRequest.iD_Job);
                }
                return this.#purchaseOrdersService.viewOrderById(quoteRequest.iD_PurchaseOrder, quoteRequest.iD_Job).catch(() => {});
            } else if (quoteRequest.iD_Variation && quoteRequest.iD_Job) {
                if (!this.job() || this.job()!.ID_Job !== quoteRequest.iD_Job) {
                    // Need to load job entity so breeze mapping works here
                    await this.#jobsRepo.GetJob(quoteRequest.iD_Job);
                }
                return this.#jobVariationsService
                    .viewVariationById({ variationId: quoteRequest.iD_Variation, jobId: quoteRequest.iD_Job })
                    .catch(() => {});
            }
        } finally {
            this.isLoading.emit(false);
        }
    }

    async addOrder(quoteRequest: QuoteRequestListModel, type: string) {
        const selectedRecipient = quoteRequest.quoteRequestRecipients?.find((qrr) => qrr?.isSelectedQuote);
        if (selectedRecipient && quoteRequest.iD_Job) {
            this.isLoading.emit(true);
            const res = await this.#quoteRequestsService.getQuoteRequestResponseData(
                quoteRequest.iD_QuoteRequest,
                quoteRequest.iD_Job,
                selectedRecipient.iD_QuoteRequestRecipient
            );
            this.isLoading.emit(false);
            if (type === this.rfqActions.Variation) {
                await this.setRFQResponseDataForVariation(res.recipientItems, res.jobItems, res.quoteRequest);
            } else {
                await this.setRFQResponseDataForPO(res.recipientItems, res.jobItems, res.purchaseOrders, res.quoteRequest, type);
            }
        }
    }

    private async setRFQResponseDataForPO(
        qrRecpItems: BreezeCase<QuoteRequestRecipientItem>[],
        jobItems: BreezeCase<JobItem>[],
        purchaseOrders: BreezeCase<PurchaseOrder>[],
        quoteRequest: BreezeCase<QuoteRequest>,
        type: string
    ) {
        const items: any[] = [];
        const selectedRecipient = quoteRequest.QuoteRequestRecipients?.find((i) => {
            return i.IsSelectedQuote;
        });

        const itemsToActualsMap = this.loadItemOrders(jobItems, purchaseOrders);

        qrRecpItems
            .sort((a, b) => (a.OrderNumber ?? 0) - (b.OrderNumber ?? 0))
            .forEach((item: BreezeCase<QuoteRequestRecipientItem>) => {
                if (item.IsSelected) {
                    let parent = null;
                    let jobItem = null;

                    const quoteRequestItem = quoteRequest.QuoteRequestItems?.find(
                        (qr) => qr.ID_QuoteRequestItem === item.ID_QuoteRequestItem
                    );
                    if (!quoteRequest.IsStandalone && quoteRequestItem) {
                        parent = this.findAndSetParent(quoteRequest, quoteRequestItem);
                        jobItem = this.getJobItem(quoteRequestItem, jobItems);
                    }

                    let orderedQty = 0;
                    let orderedCost = 0;

                    if (jobItem) {
                        const actualOrderItems = itemsToActualsMap.get(jobItem);
                        actualOrderItems?.forEach((actualorderitem) => {
                            if (actualorderitem.order.OrderStatus !== 'Cancelled' && actualorderitem?.order.IsDeleted === false) {
                                orderedQty += actualorderitem.quantity;
                                orderedCost = Round(orderedCost + actualorderitem.totalCost, 2);
                            }
                        });
                    }

                    let qtyToOrder = item.Quantity - orderedQty;
                    const recipientUnitCost = this.#quoteRequestsService.getUnitCostForRFQResponseItem(item);
                    let unitCost = jobItem?.ActualUnitCost || recipientUnitCost;

                    if (jobItem && jobItem.EstimatedQty === 1 && item.Quantity === 1 && orderedQty > 0) {
                        qtyToOrder = 1;
                        unitCost = Math.max(0, Round(recipientUnitCost - orderedCost, 4));
                    }

                    const costItemType = item.CostItemType ? item.CostItemType : this.materialCostItem;
                    const taxRate =
                        jobItem?.TaxRates ||
                        this.#quoteRequestsService.getDefaultTaxForNewItem(this.row().jobView!.taxRates!, costItemType);

                    items.push({
                        Description: item.Description,
                        ID_CostItem: jobItem?.ID_CostItem,
                        UOM: item.UOM,
                        Quantity: qtyToOrder,
                        Units: qtyToOrder,
                        UnitCost: unitCost,
                        ID_EstimateItem: jobItem?.ID_EstimateItem,
                        ID_JobItem: quoteRequestItem?.ID_JobItem,
                        ParentTask: parent?.Description,
                        ID_ParentTask: parent?.ID_JobItem,
                        ItemCode: jobItem?.ItemCode,
                        CostItemType: costItemType,
                        TaxRates: taxRate,
                        TaxContent: jobItem?.TaxContent,
                        Notes: jobItem?.Notes,
                    });
                }
            });

        await this.createPOTypesFromRFQ(
            {
                OrderType: type,
                ID_Contact: selectedRecipient!.ID_Contact,
                CreationSource: PurchaseOrderCreationSource.JOBRFQ,
            },
            items,
            quoteRequest
        );
    }

    private async setRFQResponseDataForVariation(
        qrRecpItems: BreezeCase<QuoteRequestRecipientItem>[],
        jobItems: BreezeCase<JobItem>[],
        quoteRequest: BreezeCase<QuoteRequest>
    ) {
        const items: any[] = [];

        qrRecpItems
            .sort((a, b) => (a.OrderNumber ?? 0) - (b.OrderNumber ?? 0))
            .forEach((item: BreezeCase<QuoteRequestRecipientItem>) => {
                if (item.IsSelected) {
                    let parent = null;
                    let jobItem = null;

                    const quoteRequestItem = quoteRequest.QuoteRequestItems?.find(
                        (qr) => qr.ID_QuoteRequestItem === item.ID_QuoteRequestItem
                    );
                    if (!quoteRequest.IsStandalone && quoteRequestItem) {
                        parent = this.findAndSetParent(quoteRequest, quoteRequestItem);
                        jobItem = this.getJobItem(quoteRequestItem, jobItems);
                    }

                    if (jobItem) {
                        let variationQty = jobItem.EstimatedQty * -1;
                        let unitCost = jobItem.EstimatedUnitCost || Round(jobItem.EstimatedCost / jobItem.EstimatedQty, 2);

                        if (jobItem.ActualQty && jobItem.ActualQty !== jobItem.EstimatedQty) {
                            variationQty = jobItem.ActualQty - jobItem.EstimatedQty;
                            if (!jobItem.EstimatedCost)
                                unitCost = jobItem.ActualUnitCost || Round(jobItem.ActualCost / jobItem.ActualQty, 2);
                        } else {
                            if (jobItem.ActualCost && jobItem.ActualCost !== jobItem.EstimatedCost) {
                                variationQty = 1;
                                unitCost = jobItem.ActualCost - jobItem.EstimatedCost;
                            } else if (jobItem.ActualCost && jobItem.ActualCost === jobItem.EstimatedCost) {
                                variationQty = 0;
                                unitCost = jobItem.EstimatedUnitCost;
                            }
                        }

                        items.push({
                            Description: jobItem.Description,
                            ID_CostItem: jobItem.ID_CostItem,
                            UOM: jobItem.UOM,
                            Units: variationQty,
                            UnitCost: unitCost,
                            ID_EstimateItem: jobItem.ID_EstimateItem,
                            ID_JobItem: jobItem.ID_JobItem,
                            ParentTask: parent ? parent.Description : null,
                            ID_ParentTask: parent ? parent.ID_JobItem : null,
                            ItemCode: jobItem.ItemCode,
                            CostItemType: jobItem.CostItemType,
                            TaxRates: jobItem.TaxRates,
                            TaxContent: jobItem.TaxContent,
                        });
                    }

                    const unitCost = this.#quoteRequestsService.getUnitCostForRFQResponseItem(item);
                    const costItemType = item.CostItemType ? item.CostItemType : this.materialCostItem;
                    const taxRate =
                        jobItem?.TaxRates ||
                        this.#quoteRequestsService.getDefaultTaxForNewItem(this.row().jobView!.taxRates!, costItemType);

                    items.push({
                        Description: item.Description,
                        ID_CostItem: jobItem?.ID_CostItem,
                        UOM: item.UOM,
                        Quantity: item.Quantity,
                        Units: item.Quantity,
                        UnitCost: unitCost,
                        ID_EstimateItem: jobItem?.ID_EstimateItem,
                        ID_JobItem: quoteRequestItem?.ID_JobItem,
                        ParentTask: parent?.Description,
                        ID_ParentTask: parent?.ID_JobItem,
                        ItemCode: jobItem?.ItemCode,
                        CostItemType: costItemType,
                        TaxRates: taxRate,
                        TaxContent: jobItem?.TaxContent,
                        Notes: jobItem?.Notes,
                    });
                }
            });

        await this.createVariationfromRFQ(items, quoteRequest);
    }

    private async createPOTypesFromRFQ(initData: any, items: any[], quoteRequest: BreezeCase<QuoteRequest>) {
        // Job may already be loaded and provided
        let breezeJob = this.job();
        if (!breezeJob) {
            const jobId = this.row().iD_Job!;
            const breezeJobResult = await this.#jobsRepo.GetJob(jobId);
            breezeJob = breezeJobResult.results[0] as unknown as Job;
        }
        try {
            const purchaseOrder = await this.#purchaseOrdersService.newOrder(breezeJob, initData, items);
            quoteRequest.ID_PurchaseOrder = purchaseOrder.ID_PurchaseOrder;
            await this.#quoteReqRepo.Save(quoteRequest);
            // Set on the main row so the view is updated
            this.row().iD_PurchaseOrder = purchaseOrder.ID_PurchaseOrder;
        } catch {
            // cancelled
        }
    }

    private async createVariationfromRFQ(items: any[], quoteRequest: BreezeCase<QuoteRequest>) {
        // Job may already be loaded and provided
        let breezeJob = this.job();
        if (!breezeJob) {
            const jobId = this.row().iD_Job!;
            const breezeJobResult = await this.#jobsRepo.GetJob(jobId);
            breezeJob = breezeJobResult.results[0] as unknown as Job;
        }
        try {
            const variation = await this.#jobVariationsService.newVariation(breezeJob, {}, items);
            quoteRequest.ID_Variation = variation.ID_Variation;
            await this.#quoteReqRepo.Save(quoteRequest);
            // Set on the main row so the view is updated
            this.row().iD_Variation = variation.ID_Variation;
        } catch {
            // cancelled
        }
    }

    private findAndSetParent(quoteRequest: BreezeCase<QuoteRequest>, quoteRequestItem: BreezeCase<QuoteRequestItem>) {
        return quoteRequest.QuoteRequestGroups?.find((group) => group.ID_QuoteRequestGroup === quoteRequestItem.ID_QuoteRequestGroup);
    }

    private getJobItem(quoteRequestItem: BreezeCase<QuoteRequestItem>, jobItems: BreezeCase<JobItem>[]) {
        return jobItems.find((item) => item.ID_JobItem === quoteRequestItem.ID_JobItem);
    }

    private loadItemOrders(
        jobItems: BreezeCase<JobItem>[],
        purchaseOrders: BreezeCase<PurchaseOrder>[]
    ): Map<BreezeCase<JobItem>, PurchaseOrderItemMap[]> {
        let actualOrderItems: PurchaseOrderItemMap[] = [];

        purchaseOrders.forEach((order) => {
            if (order.OrderStatus === 'Received' || order.OrderStatus === 'PartReceived') {
                if (order.PurchaseOrderInvoices && order.PurchaseOrderInvoices.length) {
                    order.PurchaseOrderInvoices.forEach((orderInvoice) => {
                        if (!orderInvoice.IsDeleted) {
                            actualOrderItems = actualOrderItems.concat(
                                orderInvoice.PurchaseOrderInvoiceItems.map((invoiceItem) => {
                                    return {
                                        quantity: invoiceItem.Quantity,
                                        totalCost: invoiceItem.TotalCost,
                                        order: order,
                                        orderItem: order.PurchaseOrderItems.find((i) => {
                                            return i.ID_PurchaseOrderItem === invoiceItem.ID_PurchaseOrderItem;
                                        }),
                                    };
                                })
                            );
                        }
                    });
                } else {
                    if (order.OrderStatus === 'Received') {
                        actualOrderItems = actualOrderItems.concat(
                            order.PurchaseOrderItems.map((item) => {
                                return {
                                    quantity: item.Quantity,
                                    totalCost: item.TotalCost,
                                    order: order,
                                    orderItem: item,
                                };
                            })
                        );
                    }
                }
            } else {
                actualOrderItems = actualOrderItems.concat(
                    order.PurchaseOrderItems.map((item) => {
                        return {
                            quantity: item.Quantity,
                            totalCost: item.TotalCost,
                            order: order,
                            orderItem: item,
                        };
                    })
                );
            }
        });

        actualOrderItems = actualOrderItems.filter((i) => {
            return !i.orderItem || (i.orderItem && !i.orderItem.IsDeleted);
        });

        const itemsToActualsMap = new Map<BreezeCase<JobItem>, PurchaseOrderItemMap[]>();

        jobItems.forEach((item: BreezeCase<JobItem>) => {
            itemsToActualsMap.set(
                item,
                actualOrderItems.filter((i) => {
                    return (
                        ((i.orderItem && i.orderItem.ID_EstimateItem && i.orderItem.ID_EstimateItem === item.ID_EstimateItem) ||
                            (i.orderItem && i.orderItem.ID_JobItem && i.orderItem.ID_JobItem === item.ID_JobItem)) &&
                        !i.order.IsDeleted
                    );
                })
            );
        });

        return itemsToActualsMap;
    }

    getResponseIcon(row: QuoteRequestRecipientListModel) {
        switch (row.quoteStatus) {
            case QuoteStatus.Opened:
                return '<span class="label label-inner label-min-100 label-info">Opened</span>';
            case QuoteStatus.Quoting:
                return '<span class="label label-inner label-min-100 label-info">Quoting</span>';
            case QuoteStatus.Accepted:
                return '<span class="label label-inner label-min-100 label-primary">Accepted</span>';
            case QuoteStatus.Declined:
                return '<span class="label label-inner label-min-100 label-gray">Declined</span>';
            case QuoteStatus.Pending:
                return '<span class="label label-inner label-min-100 label-warning">Pending</span>';
            case QuoteStatus.Submitted:
                return '<span class="label label-inner label-min-100 label-info">Submitted</span>';
            case QuoteStatus.Notified:
                return '<span class="label label-inner label-min-100 label-gray">Notified</span>';
            default:
                return row.quoteStatus;
        }
    }
}
